export const GROUP_DESCRIPTION_MIN_LENGTH = 10;
export const EMBLEM_SEPARATOR = '|||';

/**
 * Encodes emblem into description field using separator.
 * Format: "🐉|||Описание группы"
 */
export function encodeEmblemInDescription(emblem: string, description: string): string {
  return `${emblem}${EMBLEM_SEPARATOR}${description}`;
}

/**
 * Parses emblem and description from encoded string.
 * Returns { emblem, description } or defaults if no emblem found.
 */
export function parseEmblemFromDescription(encodedDescription: string): { emblem: string; description: string } {
  if (!encodedDescription || !encodedDescription.includes(EMBLEM_SEPARATOR)) {
    return { emblem: '🛡️', description: encodedDescription || '' };
  }

  const separatorIndex = encodedDescription.indexOf(EMBLEM_SEPARATOR);
  const emblem = encodedDescription.slice(0, separatorIndex);
  const description = encodedDescription.slice(separatorIndex + EMBLEM_SEPARATOR.length);

  return { emblem, description };
}

export type GroupFormDraft = {
  name: string;
  emblem: string;
  description: string;
};

export type GroupFormErrors = {
  name: string | null;
  emblem: string | null;
  description: string | null;
};

export type GroupFormValidationResult = {
  errors: GroupFormErrors;
  isValid: boolean;
  normalizedName: string;
  normalizedDescription: string;
};

export function validateGroupFormDraft(draft: GroupFormDraft): GroupFormValidationResult {
  const normalizedName = draft.name.trim();
  const normalizedDescription = draft.description.trim();

  const nameError = (() => {
    if (normalizedName.length < 3) return 'Минимум 3 символа';
    if (normalizedName.length > 50) return 'Максимум 50 символов';
    return null;
  })();

  const emblemError = (() => {
    const validEmblems = ['🛡️', '⚔️', '🏹', '🔮', '⚡', '🌟', '🐉', '🦅', '🐺', '👑'];
    if (!validEmblems.includes(draft.emblem)) return 'Выберите эмблему';
    return null;
  })();

  const descriptionError = (() => {
    if (normalizedDescription.length < GROUP_DESCRIPTION_MIN_LENGTH) return `Минимум ${GROUP_DESCRIPTION_MIN_LENGTH} символов`;
    if (normalizedDescription.length > 300) return 'Максимум 300 символов';
    return null;
  })();

  const isValid = nameError == null && emblemError == null && descriptionError == null;

  return {
    errors: { name: nameError, emblem: emblemError, description: descriptionError },
    isValid,
    normalizedName,
    normalizedDescription,
  };
}
